import { useRef, useState } from 'react';

export type Helpers = {
  /* When set to true, modal should be visible, otherwise it should be hidden */
  isDialogShown: boolean;

  /** To be called when the confirm button on the modal is clicked.
   *
   * When called, the callback parameter passed to the confirm function is run, and
   * isDialogShown is set to false.
   */
  handleConfirm: () => void;

  /** To be called when the cancel button on the modal is clicked
   *
   * Sets isDialogShown to false when called.
   */
  handleCancel: () => void;

  /**
   *  Sets isDialogShown to true, and potentially runs the confirmAction callback.
   *  @param confirmAction - callback is only run when user clicks on the confirm button.
   */
  confirm: (confirmAction: () => void) => void;
};

export type ConfirmDialogProps = {
  children: (helpers: Helpers) => JSX.Element;
};

/**
 * Higher-order component that accepts props with a children function taking a ConfirmDialogProps
 * parameter and returning a JSX.Element.
 *
 * The props can be used by form elements and a modal to orchestrate behavior that
 * only occurs after the user confirms it should happen.
 *
 * @param children
 */
export function ConfirmDialogHelper({ children }: ConfirmDialogProps) {
  const [isDialogShown, setIsDialogShown] = useState<boolean>(false);
  const confirmAction = useRef<() => void | undefined>();

  const reset = () => {
    confirmAction.current = undefined;
    setIsDialogShown(false);
  };

  return children({
    isDialogShown: isDialogShown,
    handleConfirm: () => {
      if (confirmAction.current) {
        confirmAction.current();
        reset();
      }
    },
    handleCancel: () => {
      reset();
    },
    confirm: (confirmActionCallback) => {
      confirmAction.current = confirmActionCallback;
      setIsDialogShown(true);
    },
  });
}
